import { create } from "zustand";
import { devtools } from "zustand/middleware";
import axios from "axios";
import { produce } from "immer";
import { findIndex } from "lodash";
import fileDownload from "js-file-download";

const initialState = {
  gettingIncomeStatement: false,
  incomeStatementError: {},
  incomeStatement: [],
  downloadingIncomeStatement: false,
  downloadIncomeStatementError: {},
  // project-level
  gettingProjectIncomeStatement: false,
  projectIncomeStatementError: {},
  projectIncomeStatement: [],
  downloadingProjectIncomeStatement: false,
  downloadProjectIncomeStatementError: {},
};

const store = (set, get) => ({
  ...initialState,

  async getIncomeStatement(context) {
    set({ gettingIncomeStatement: true, incomeStatementError: {} });
    try {
      const endpoint = "/finance/income-statement".replace(/\/$/, "");
      const res = await axios.get(endpoint, {
        params: {
          startDate: context.startDate,
          endDate: context.endDate,
        },
      });

      // Handle response structure - http.serverSuccess wraps the response
      // Backend returns: { success: true, message: "...", data: { income: {...}, expenditure: {...}, surplusDeficit: ... } }
      // Axios interceptor returns response.data, so res is the wrapped object
      const responseData = res?.data || res;

      const records = get().incomeStatement;
      const index = findIndex(
        records,
        (record) =>
          record.startDate === context.startDate &&
          record.endDate === context.endDate
      );

      if (index > -1) {
        set(
          produce((state) => {
            state.incomeStatement[index] = {
              data: responseData,
              ...context,
            };
          })
        );
      } else {
        set(
          produce((state) => {
            state.incomeStatement.push({
              data: responseData,
              ...context,
            });
          })
        );
      }
    } catch (err) {
      console.error("Income Statement API Error:", err);
      console.error("Error Details:", {
        status: err?.response?.status,
        statusText: err?.response?.statusText,
        data: err?.response?.data,
        message: err?.message,
      });
      console.error("Request URL:", `/finance/income-statement?startDate=${context.startDate}&endDate=${context.endDate}`);
      set({ incomeStatementError: err });
    } finally {
      set({ gettingIncomeStatement: false });
    }
  },

  async getProjectIncomeStatement(context) {
    set({
      gettingProjectIncomeStatement: true,
      projectIncomeStatementError: {},
    });
    try {
      const endpoint = `/finance/income-statement/project/${context.projectId}`.replace(
        /\/$/,
        ""
      );
      const res = await axios.get(endpoint, {
        params: {
          startDate: context.startDate,
          endDate: context.endDate,
        },
      });

      const responseData = res?.data || res;

      const records = get().projectIncomeStatement;
      const index = findIndex(
        records,
        (record) =>
          record.startDate === context.startDate &&
          record.endDate === context.endDate &&
          record.projectId === context.projectId
      );

      if (index > -1) {
        set(
          produce((state) => {
            state.projectIncomeStatement[index] = {
              data: responseData,
              ...context,
            };
          })
        );
      } else {
        set(
          produce((state) => {
            state.projectIncomeStatement.push({
              data: responseData,
              ...context,
            });
          })
        );
      }
    } catch (err) {
      console.error("Project Income Statement API Error:", err);
      set({ projectIncomeStatementError: err });
    } finally {
      set({ gettingProjectIncomeStatement: false });
    }
  },

  async downloadIncomeStatement(context, fileName = "INCOME_STATEMENT.xlsx") {
    set({ downloadingIncomeStatement: true, downloadIncomeStatementError: {} });
    await axios
      .get("/finance/income-statement/download-excel", {
        params: {
          startDate: context.startDate,
          endDate: context.endDate,
        },
        responseType: "blob",
      })
      .then((res) => {
        fileDownload(res.data, fileName);
      })
      .catch((err) => {
        set({ downloadIncomeStatementError: err });
      })
      .finally(() => set({ downloadingIncomeStatement: false }));
  },

  async downloadProjectIncomeStatement(
    context,
    fileName = "PROJECT_INCOME_STATEMENT.xlsx"
  ) {
    set({
      downloadingProjectIncomeStatement: true,
      downloadProjectIncomeStatementError: {},
    });
    await axios
      .get("/finance/income-statement/download-excel", {
        params: {
          startDate: context.startDate,
          endDate: context.endDate,
          projectId: context.projectId,
        },
        responseType: "blob",
      })
      .then((res) => {
        fileDownload(res.data, fileName);
      })
      .catch((err) => {
        set({ downloadProjectIncomeStatementError: err });
      })
      .finally(() => set({ downloadingProjectIncomeStatement: false }));
  },
});

const storeName = "Income Statement Store";

const useIncomeStatementStore = create(devtools(store, { storeName }), { storeName });

export default useIncomeStatementStore;



